import React from 'react'
import { connect } from 'react-redux'
import cn from 'classnames'
import { injectIntl } from 'react-intl'
import { Switch } from '@material-ui/core'
import isEqual from 'lodash/isEqual'
import ListFromTable from 'components/common/ListFromTable'
import {
  saveListForm,
  setIsLoad,
  setPsfs,
} from 'reducers/projectedSitesManager/reducer'
import Psfs from 'lib/psfs/Psfs'
import { ReactComponent as SingleSite } from 'assets/img/icon-chart-statistic.svg'
import ColumnCollection from 'types/table/ColumnCollection'
import {
  FRESHWATER_FIELDS,
  SEAWATER_SITE_LIST_FIELDS,
} from 'constants/projectedSitesManager'
import Column from 'types/table/Column'
import Sort from 'lib/psfs/Sort'
import withPromise from 'helpers/withPromise'
import UrlHelper from 'helpers/url'
import * as ROUTE from 'constants/appRouting'
import { SITE_TYPE_FRESHWATER, SITE_TYPE_SEAWATER } from 'constants/index'
import ProfileHelper from 'helpers/profile'
import { Flex } from 'components/common/styles'
import messages from '../translate'

class ProjectedSitesManagerTable extends ListFromTable {
  constructor(props) {
    super(props)
    this.asyncData = true
    this.loadingData = true

    this.state = {
      ...this.state,
    }

    this.init()
  }

  componentDidUpdate() {
    const { isLoadTable } = this.props

    this.loadingData = isLoadTable
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { storePsfs, tableData, history } = this.props
    const {
      storePsfs: nextPsfs,
      tableData: nextTableData,
      isLoadTable,
    } = nextProps

    this.loadingData = isLoadTable

    if (!isEqual(tableData, nextTableData)) {
      this.loadingData = false
    }

    if (!isEqual(storePsfs, nextPsfs)) {
      history.push(nextPsfs.path)
    }

    return super.shouldComponentUpdate(nextProps, nextState, nextContext)
  }

  init = () => {
    const { storePsfs, setPsfs } = this.props

    this.psfs = new Psfs(storePsfs.path)
    this.historyPush = path => {
      this.setState({ pathname: path })
      setPsfs(new Psfs(path))
    }
  }

  getTableProps() {
    const {
      tableData,
      onEditData,
      storePsfs,
      siteType,
      selectedSnapshot,
    } = this.props
    const tableDataList = tableData.toJS()
    const tableDataLength =
      Object.prototype.hasOwnProperty.call(tableDataList, 'count') &&
      tableDataList.count
    const isEditable = siteType === SITE_TYPE_SEAWATER
    const isSnapshot = selectedSnapshot !== 'main-table'

    return {
      title: '',
      className: cn({
        'vertical-scroll': tableDataLength > 10,
        'is-editable': isEditable,
        snapshot: isSnapshot,
      }),
      showFilterBar: false,
      sorting: true,
      paging: true,
      dense: {
        enabled: false,
        state: true,
        handler: () => {},
      },
      hasStorePsfs: true,
      stickyHeader: true,
      storePsfs,
      onRowClick: row => {
        if (isEditable) {
          onEditData(row)
        }
      },
    }
  }

  getColumns() {
    const {
      storePsfs,
      history,
      intl: { formatMessage },
      saveListForm,
      onPanelClose,
      setIsLoad,
      siteType,
      selectedSnapshot,
    } = this.props
    const {
      _field,
      _order: [orderType],
    } = storePsfs.sortObj
    const tableFields =
      siteType === SITE_TYPE_SEAWATER
        ? SEAWATER_SITE_LIST_FIELDS.sort((a, b) => a.id - b.id)
        : FRESHWATER_FIELDS.sort((a, b) => a.id - b.id)

    const getSorting = storeName => {
      const sorting = {}

      if (_field && _field === storeName) {
        sorting.defaultSort = _field
        sorting.defaultSortOrder = Sort[orderType.toUpperCase()]
      }

      return sorting
    }

    const handleSwitchChange = (id, enabled) => {
      withPromise(saveListForm, {
        data: {
          enabled: !enabled,
        },
        id,
        sucessMessage: formatMessage(messages.siteUpdatedSuccessfully),
        isEditMode: true,
        siteType,
        snapshot: selectedSnapshot,
      }).then(onPanelClose)
    }

    const diveToSiteOrClient = (event, id, specie, year) => {
      const route =
        siteType === SITE_TYPE_SEAWATER
          ? ROUTE.PROJECTED_SITES_MANAGER_SEAWATER_SITE
          : ROUTE.PROJECTED_SITES_MANAGER_FRESHWATER_CLIENT
      const path = UrlHelper.create(route, {
        id,
        type: siteType,
        snapshot: selectedSnapshot,
        specie,
        year,
      })

      event.stopPropagation()
      onPanelClose()
      setIsLoad(true)
      history.push(path)
    }

    const columns = new ColumnCollection()

    columns.add(
      new Column('actions', formatMessage(messages.actions), {
        sorting: false,
        render: ({
          id,
          enabled,
          siteId,
          clientId,
          specieId,
          yearClass,
          hasProjections,
        }) => (
          <Flex align="center">
            <SingleSite
              className={cn('go-to-site-btn', {
                'go-to-site-btn-disabled':
                  !hasProjections && siteType === SITE_TYPE_FRESHWATER,
              })}
              onClick={event => {
                if (!hasProjections && siteType === SITE_TYPE_FRESHWATER) {
                  return
                }

                diveToSiteOrClient(
                  event,
                  siteId || clientId,
                  specieId,
                  yearClass,
                )
              }}
            />
            <Switch
              href={null}
              color="primary"
              checked={enabled}
              onClick={event => {
                event.stopPropagation()
              }}
              onChange={() => {
                handleSwitchChange(id, enabled)
              }}
            />
          </Flex>
        ),
      }),
    )

    columns.add(
      new Column('id', 'ID', {
        sorting: true,
        cellClassName: 'logbook-event-arrow-cell',
        render: rowData => rowData.id,
      }),
    )

    tableFields.forEach(({ storeName, translateName }) => {
      const sortParams = getSorting(storeName)

      columns.add(
        new Column(
          storeName,
          formatMessage(messages[translateName]),
          {
            sorting: true,
            ...sortParams,
            render: rowData => {
              const additionalData =
                storeName === 'site' && rowData.siep ? ` - ${rowData.siep}` : ''
              let message = rowData[storeName]
              const rightAlignedColumns = [
                'stockingMonthYear',
                'stockingFishCount',
                'stockingWeight',
                'monthsFromStocking',
                'numberOfCages',
                'harvestingMonthYear',
                'harvestingLength',
              ]

              if (
                message &&
                typeof message === 'number' &&
                storeName !== 'yearClass'
              ) {
                const { code: currentCode } = ProfileHelper.getCurrentLocale()

                message = message.toLocaleString(currentCode)
              }

              return (
                <span
                  className={cn('text-container', {
                    'align-right': rightAlignedColumns.includes(storeName),
                  })}>
                  {(message || '') + additionalData}
                </span>
              )
            },
          },
          true,
        ),
      )
    })

    return columns
  }
}

export default connect(
  state => ({
    isLoadTable: state.getIn(['projectedSitesManager', 'isLoadTable']),
    tableData: state.getIn(['projectedSitesManager', 'listTableData']),
    storePsfs: state.getIn(['projectedSitesManager', 'storePsfs']),
  }),
  {
    setPsfs,
    setIsLoad,
    saveListForm,
  },
)(injectIntl(ProjectedSitesManagerTable))
